'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  getBusinessReportFinancialCalendar,
  resetBusinessReportFinancialCalendar,
  updateBusinessReportFinancialCalendar,
  type BusinessReportFinancialCalendarResponse,
} from '../lib/api/business-reports';
import { AdminAlertModal, Loader } from './common';
import { FinanceField, FinanceModal } from './BusinessReportFinanceUi';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

type CalendarForm = {
  calendarName: string;
  startMonth: string;
  startDay: string;
  timezone: string;
  isActive: boolean;
};

const EMPTY_FORM: CalendarForm = {
  calendarName: 'Business Financial Year',
  startMonth: '',
  startDay: '',
  timezone: 'Asia/Kolkata',
  isActive: true,
};

export function BusinessReportFinancialCalendarTab({ active, token, onTopBarActionsChange }: { active: boolean; token?: string; onTopBarActionsChange?: (actions: ReactNode) => void }) {
  const [calendar, setCalendar] = useState<BusinessReportFinancialCalendarResponse | null>(null);
  const [form, setForm] = useState<CalendarForm>(EMPTY_FORM);
  const [previewYear, setPreviewYear] = useState(String(new Date().getFullYear()));
  const [loading, setLoading] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [resetReason, setResetReason] = useState('');
  const [alert, setAlert] = useState<{ type: 'success' | 'error' | 'info'; title: string; message: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getBusinessReportFinancialCalendar(token);
      setCalendar(response);
      if (response.setting) {
        setForm({
          calendarName: response.setting.calendarName,
          startMonth: String(response.setting.financialYearStartMonth),
          startDay: String(response.setting.financialYearStartDay),
          timezone: response.setting.timezone,
          isActive: response.setting.isActive,
        });
      }
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not load Financial Calendar', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (active) void load();
  }, [active, load]);

  const preview = useMemo(() => {
    const year = Number(previewYear);
    const month = Number(form.startMonth);
    const day = Number(form.startDay);
    if (!Number.isInteger(year) || year < 1900 || year > 9998 || !validMonthDay(month, day)) return [];
    return [0, 3, 6, 9].map((offset, index) => {
      const start = dateAtOffset(year, month, day, offset);
      const next = dateAtOffset(year, month, day, offset + 3);
      const end = new Date(next.getTime() - 86_400_000);
      return {
        quarter: `Q${index + 1}`,
        startDate: start.toISOString().slice(0, 10),
        endDate: end.toISOString().slice(0, 10),
      };
    });
  }, [form.startDay, form.startMonth, previewYear]);

  useEffect(() => {
    if (!active || !onTopBarActionsChange) return undefined;
    const value = calendar?.configured ? (calendar.setting?.isActive ? 'ACTIVE' : 'INACTIVE') : 'NOT CONFIGURED';
    onTopBarActionsChange(<div className="business-calendar-top-status"><span>Calendar</span><strong className={`business-source-status ${calendar?.setting?.isActive ? 'active' : 'inactive'}`}>{value}</strong></div>);
    return () => onTopBarActionsChange(null);
  }, [active, calendar?.configured, calendar?.setting?.isActive, onTopBarActionsChange]);

  if (!active) return null;

  const save = async () => {
    const month = Number(form.startMonth);
    const day = Number(form.startDay);
    if (!validMonthDay(month, day)) {
      setAlert({
        type: 'error',
        title: 'Invalid financial-year start',
        message: 'Select a valid start month and day before saving.',
      });
      return;
    }
    setLoading(true);
    try {
      const response = await updateBusinessReportFinancialCalendar({
        calendarName: cleanSpaces(form.calendarName) || 'Business Financial Year',
        financialYearStartMonth: month,
        financialYearStartDay: day,
        timezone: form.timezone.trim() || 'Asia/Kolkata',
        isActive: form.isActive,
      }, token);
      setCalendar(response);
      setAlert({
        type: 'success',
        title: 'Financial Calendar saved',
        message: 'The financial-year start and derived Q1–Q4 definition were audited.',
      });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not save Financial Calendar', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  const reset = async () => {
    const reason = cleanSpaces(resetReason);
    if (!reason) {
      setAlert({ type: 'error', title: 'Reset reason required', message: 'Enter why this inactive configuration is unapproved or invalid.' });
      return;
    }
    setLoading(true);
    try {
      const response = await resetBusinessReportFinancialCalendar(reason, token);
      setCalendar(response);
      setForm(EMPTY_FORM);
      setResetting(false);
      setResetReason('');
      setAlert({ type: 'success', title: 'Financial Calendar reset', message: 'The inactive unapproved configuration was removed with mandatory transactional audit evidence.' });
    } catch (error) {
      setAlert({ type: 'error', title: 'Could not reset Financial Calendar', message: errorMessage(error) });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="tab-content business-source-page" id="tab-business-report-financial-calendar">
      {loading && <Loader message="Processing Business Reports Financial Calendar..." />}

      <div className="enterprise-card business-calendar-definition-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Quarter Definition</strong>
            <span>{calendar?.definition.rule || 'Q1–Q4 are four consecutive three-month periods from the configured financial-year start.'}</span>
          </div>
        </div>
        <div style={{ padding: 20 }}>
          <p style={{ margin: 0, color: 'var(--text-muted)' }}>
            Q1 starts on the configured date; Q2 starts three months later, Q3 after six months and Q4 after nine months.
            Each quarter ends one day before the next quarter begins.
          </p>
        </div>
      </div>

      <div className="card enterprise-filter-card business-calendar-config-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Financial-year Configuration</strong>
            <span>No start date is assumed. An authorized administrator must save the approved business setting.</span>
          </div>
        </div>
        <div className="enterprise-filter-grid business-calendar-config-grid">
          <label className="enterprise-field">
            <span>Calendar Name</span>
            <input value={form.calendarName} onChange={(event) => setForm((current) => ({ ...current, calendarName: event.target.value }))} />
          </label>
          <label className="enterprise-field">
            <span>Financial Year Start Month</span>
            <select value={form.startMonth} onChange={(event) => setForm((current) => ({ ...current, startMonth: event.target.value }))}>
              <option value="">Select month</option>
              {MONTHS.map((month, index) => <option key={month} value={index + 1}>{month}</option>)}
            </select>
          </label>
          <label className="enterprise-field">
            <span>Financial Year Start Day</span>
            <input type="number" min="1" max="31" value={form.startDay} onChange={(event) => setForm((current) => ({ ...current, startDay: event.target.value }))} />
          </label>
          <label className="enterprise-field">
            <span>Timezone</span>
            <input value={form.timezone} onChange={(event) => setForm((current) => ({ ...current, timezone: event.target.value }))} />
            <small>Use an IANA timezone such as Asia/Kolkata.</small>
          </label>
          <label className="business-calendar-active-toggle">
            <input type="checkbox" checked={form.isActive} onChange={(event) => setForm((current) => ({ ...current, isActive: event.target.checked }))} />
            <span><strong>Active calendar</strong><small>Use this definition for Business Reports periods.</small></span>
          </label>
        </div>
        <div className="business-calendar-config-footer">
          <span>Changes affect future period derivation and are recorded in the audit log.</span>
          <div><button className="btn btn-outline" disabled={!calendar?.setting || calendar.setting.isActive} onClick={() => setResetting(true)}>Reset Inactive Configuration</button> <button className="btn btn-primary" onClick={() => void save()}>Save Financial Calendar</button></div>
        </div>
      </div>

      <div className="enterprise-card enterprise-table-card business-calendar-preview-card">
        <div className="enterprise-card-heading">
          <div>
            <strong>Quarter Preview</strong>
            <span>Preview only; changing this year does not change the saved annual start rule.</span>
          </div>
          <label className="business-source-field" style={{ margin: 0, minWidth: 160 }}>
            <span>Start Year</span>
            <input type="number" min="1900" max="9998" value={previewYear} onChange={(event) => setPreviewYear(event.target.value)} />
          </label>
        </div>
        <div className="enterprise-table-wrap">
          <table className="enterprise-table">
            <thead><tr><th>Quarter</th><th>Start Date</th><th>End Date</th><th>Definition</th></tr></thead>
            <tbody>
              {preview.map((period) => (
                <tr key={period.quarter}>
                  <td><strong>{period.quarter}</strong></td>
                  <td>{period.startDate}</td>
                  <td>{period.endDate}</td>
                  <td>Three consecutive months</td>
                </tr>
              ))}
              {!preview.length && <tr><td colSpan={4} className="empty-cell">Choose a valid financial-year start month and day to preview Q1–Q4.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {resetting && <FinanceModal title="Reset Inactive Financial Calendar" subtitle="Use this only for an unapproved or test configuration. Active settings cannot be reset." onClose={() => setResetting(false)} footer={<><span>The previous value and reset actor are written to mandatory audit in the same transaction.</span><div><button className="btn btn-outline" onClick={() => setResetting(false)}>Cancel</button><button className="btn btn-primary" onClick={() => void reset()}>Confirm Reset</button></div></>}><FinanceField label="Reset Reason" full><textarea rows={4} value={resetReason} onChange={(event) => setResetReason(event.target.value)} /></FinanceField></FinanceModal>}
      {alert && <AdminAlertModal title={alert.title} message={alert.message} type={alert.type} onClose={() => setAlert(null)} />}
    </section>
  );
}

function validMonthDay(month: number, day: number) {
  if (!Number.isInteger(month) || month < 1 || month > 12 || !Number.isInteger(day) || day < 1) return false;
  return day <= new Date(Date.UTC(2001, month, 0)).getUTCDate();
}

function dateAtOffset(startYear: number, startMonth: number, startDay: number, offsetMonths: number) {
  const absoluteMonth = startMonth - 1 + offsetMonths;
  const year = startYear + Math.floor(absoluteMonth / 12);
  const month = absoluteMonth % 12;
  const maximumDay = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(startDay, maximumDay)));
}

function cleanSpaces(value: string) {
  return value.trim().replace(/\s+/g, ' ');
}

function errorMessage(error: unknown) {
  return error instanceof Error ? error.message : 'An unexpected error occurred.';
}
